import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';

import type {
  HeadingTag,
  StructureItem,
} from './cards/StructureChecklistCard';

/**
 * useStructureSuggestions — drives StructureChecklistCard from the live TipTap
 * doc. This intentionally avoids provider calls on mount/update; structure
 * suggestions should only become AI-backed behind an explicit user action.
 *
 * Returns:
 *   items: existing H1/H2/H3 (flagged: false).
 *   onAddSuggested: inserts a level-2 heading at the end of the doc and
 *          inserts a level-2 heading at the end of the doc.
 */

export interface UseStructureSuggestionsResult {
  items: StructureItem[];
  onAddSuggested: (item: StructureItem) => void;
}

const HEADING_TAG: Record<number, HeadingTag | undefined> = {
  1: 'H1',
  2: 'H2',
  3: 'H3',
};

function walkHeadings(editor: Editor | null): StructureItem[] {
  if (!editor) return [];
  const out: StructureItem[] = [];
  let counter = 0;
  editor.state.doc.descendants((node, pos) => {
    // FAQ questions are their own node type (faqQuestion) rather than a
    // `heading` with level 3, but they render as <h3 itemprop="name"> — so
    // count them here, otherwise the outline silently omits every FAQ heading
    // and there's no way to verify the document's H3s.
    const isFaqQuestion = node.type.name === 'faqQuestion';
    if (node.type.name !== 'heading' && !isFaqQuestion) return true;
    const tag = isFaqQuestion
      ? ('H3' as HeadingTag)
      : HEADING_TAG[node.attrs.level as number];
    if (!tag) return true;
    const title = node.textContent.trim();
    if (!title) return true;
    out.push({
      id: `live-${pos}-${counter++}`,
      tag,
      title,
      flagged: false,
    });
    return true;
  });
  return out;
}

export function useStructureSuggestions(
  contentId: string,
  editor: Editor | null,
  focusKeyword: string,
): UseStructureSuggestionsResult {
  const [existing, setExisting] = useState<StructureItem[]>(() => walkHeadings(editor));
  void contentId;
  void focusKeyword;

  // Recompute on every editor update; the heading walker is cheap so a
  // microtask is enough — no debounce needed for the local list. The query
  // key below changes only when the heading text set changes, so cached
  // suggestions stay warm during keystrokes.
  useEffect(() => {
    if (!editor) {
      setExisting([]);
      return;
    }
    setExisting(walkHeadings(editor));
    const onUpdate = () => setExisting(walkHeadings(editor));
    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
    };
  }, [editor]);

  const items = useMemo<StructureItem[]>(() => {
    return existing;
  }, [existing]);

  const onAddSuggested = useCallback(
    (item: StructureItem) => {
      if (!editor) return;
      const level = item.tag === 'H1' ? 1 : item.tag === 'H2' ? 2 : 3;
      const docEnd = editor.state.doc.content.size;
      editor
        .chain()
        .focus()
        .insertContentAt(docEnd, {
          type: 'heading',
          attrs: { level },
          content: [{ type: 'text', text: item.title }],
        })
        .run();
    },
    [editor],
  );

  return { items, onAddSuggested };
}
